import { Fragment, useEffect, useState } from "react"
import PropTypes from "prop-types"
import classnames from "classnames"
import { capitalize } from "humanize-plus"
import { getUserApplets } from "shared/lib/graphql"
import { getServiceAndApplets } from "./queries"
import logIFTTTNextAction from "app/lib/ifttt_next_log_action"
import Spinner from "shared/components/spinner"
import DiscoverServiceAppletCard from "./applet_card"
import { MyServiceView } from "../my_service_view"
import RelatedServices from "../../related_services"
import SuggestionModal from "shared/components/suggestion_modal"
import plusIcon from "images/icons/plus-icon-soft.svg"
import plusIconMobile from "images/icons/plus-icon-soft-mobile.svg"
import ErrorSuccessHistogramChart from "shared/components/error_success_histogram_chart"
import PerformanceTriggerActionChart from "shared/components/performance_trigger_action_chart"
import UserFeedback from "./user_feedback"
import proPlusBadge from "images/plan-badges/pro_plus.svg"
import proBadge from "images/plan-badges/pro.svg"

import { singularFromPlural } from "shared/lib/utils"
import { serviceShape, connectionShape } from "app/lib/shared_prop_types"

import dsv from "./discover_service_view.scss"
import wwc from "./works_well_card.scss"

const tqaTypes = ["triggers", "queries", "actions"]

// HACK: calculations using this are due to Highcart limitations. The intended
// result here is ~18px of margin between bars.
const BASE_PERFORMANCE_CHART_HEIGHT = 140

export const DiscoverServiceView = ({ triggerSpeeds, actionSpeeds, ...props }) => {
  const [myApplets, setMyApplets] = useState([])
  const [applets, setApplets] = useState(props.loggedOutDiscoverApplets)
  const [currentTab, setCurrentTab] = useState(props.currentTab)
  const [feedbackTqaType, setFeedbackTqaType] = useState(null)
  const isDesktop = window.matchMedia?.(`(min-width: 724px)`)?.matches

  const signedIn = !!window.App.user?.id

  const navigateToTab = currentView => {
    setCurrentTab(currentView)
    let title
    let url
    switch (currentView) {
      case "applets":
        title = props.descriptions.service
        url = props.urls.servicePath
        break
      case "my_applets":
        title = props.descriptions.myApplets
        url = props.urls.myAppletsPath
        break
      case "details":
        title = props.descriptions.details
        url = props.urls.detailsPath
        break
      case "health":
        title = props.descriptions.health
        url = props.urls.healthPath
    }

    history.pushState({ currentView }, title, url)
  }

  const handlePopState = event => {
    if (event.state?.hasOwnProperty("currentView")) setCurrentTab(event.state.currentView)
  }

  const applyAppletsFetch = (discover, live_applets, statements) => {
    const relevantApplets = (live_applets || [])
      .concat(statements || [])
      .map(a => a.normalized_applet || a.applet.normalized_applet)
      .filter(a => a.channels.some(c => c.module_name === props.service.module_name))
    setMyApplets(relevantApplets)

    if (currentTab === "my_applets" && relevantApplets.length === 0) {
      setCurrentTab("applets")
    }

    const removedIds = relevantApplets.map(a => a.id)
    const availableApplets = discover.applets_for_channel.filter(a => !removedIds.includes(a.id))
    setApplets(availableApplets)

    if (signedIn && !relevantApplets.length && !availableApplets.length) {
      const headerCreateButton = document.querySelector(".horizontal-ctas > a[data-button-type='create']")
      if (headerCreateButton) {
        headerCreateButton.style.display = "hidden"
      }
    }
  }

  useEffect(() => {
    window.addEventListener("popstate", handlePopState)

    return () => window.removeEventListener("popstate", handlePopState)
  })

  useEffect(() => {
    if (signedIn) {
      let appletsPromises = Promise.all([
        getServiceAndApplets(props.service.module_name, props.topAppletSlugs),
        getUserApplets(),
      ])

      appletsPromises
        .then(promiseValues => {
          const [
            {
              channel: { discover },
            },
            { live_applets, statements },
          ] = promiseValues

          applyAppletsFetch(discover, live_applets, statements)
        })
        .catch(err => console.warn(err))
    }
  }, [])

  const logDiyClick = () =>
    logIFTTTNextAction("click", {
      object_type: "diy_create_button",
      location_type: "discover_service",
    })

  const Tab = ({ name, label }) => (
    <span
      role="tab"
      aria-label={label}
      onClick={() => navigateToTab(name)}
      styleName={classnames({
        "dsv.active": currentTab === name,
      })}
    >
      {label}
    </span>
  )

  return (
    <>
      {feedbackTqaType && signedIn && (
        <SuggestionModal
          channelId={props.service.id}
          onClose={() => setFeedbackTqaType(null)}
          feedbackTqaType={feedbackTqaType}
          serviceName={props.service.name}
        />
      )}
      <div styleName="dsv.tab-content dsv.filters" role="tablist">
        <Tab name="applets" label="Applets" />
        {!!myApplets.length && <Tab name="my_applets" label="My Applets" />}
        <Tab name="details" label="Details" />
        {props.healthViewEnabled && <Tab name="health" label="Health" />}
      </div>
      {currentTab === "details" ? (
        <section styleName="dsv.tab-content dsv.column">
          {tqaTypes
            .filter(tqa => props[tqa].length)
            .map(tqas => (
              <Fragment key={tqas}>
                <div styleName="dsv.header">
                  <h3>{capitalize(tqas)}</h3>
                </div>
                <ul styleName="dsv.tqa-list" className="web-applet-cards tqa">
                  {props[tqas].map(({ url, name, description, tier }, i) => (
                    <li
                      className="my-web-applet-card web-applet-card web-applet-card-template tqa"
                      styleName={tier !== "free" ? "dsv.badged" : ""}
                      key={i}
                    >
                      <a
                        className="web-applet-card applet-card-body tqa"
                        styleName="wwc.connect-card"
                        style={{ backgroundColor: props.service.brand_color }}
                        href={url}
                      >
                        <div className="content tqa">
                          <span styleName="dsv.title">{name}</span>
                          <span
                            styleName={classnames("dsv.description", {
                              "dsv.badged": tier !== "free",
                            })}
                          >
                            {description}
                          </span>
                          {tier !== "free" && (
                            <span
                              title={`This ${singularFromPlural(tqas)} has pro features`}
                              className="card-badge fixed"
                              styleName="dsv.badge"
                              dangerouslySetInnerHTML={{
                                __html: tier === "pro" ? proPlusBadge : proBadge,
                              }}
                            />
                          )}
                        </div>
                      </a>
                    </li>
                  ))}
                  {signedIn && (
                    <li className="my-web-applet-card web-applet-card web-applet-card-template tqa">
                      <a
                        className="web-applet-card applet-card-body tqa"
                        styleName="wwc.connect-card dsv.suggestion-card"
                        role="button"
                        onClick={() => setFeedbackTqaType(singularFromPlural(tqas))}
                      >
                        <div styleName="dsv.content">
                          <span
                            styleName="dsv.svg-icon"
                            dangerouslySetInnerHTML={{ __html: isDesktop ? plusIcon : plusIconMobile }}
                          />
                          <span styleName="dsv.title">Suggest new {singularFromPlural(tqas)}</span>
                        </div>
                      </a>
                    </li>
                  )}
                </ul>
              </Fragment>
            ))}
        </section>
      ) : currentTab === "my_applets" ? (
        <>
          <h3>Your {props.service.name} Applets</h3>
          {myApplets.length > 0 && (
            <section className="my_services">
              <MyServiceView applets={myApplets} />
            </section>
          )}
        </>
      ) : currentTab === "health" ? (
        <section styleName="dsv.tab-content dsv.column dsv.health">
          <UserFeedback moduleName={props.service.module_name} />

          {!!(triggerSpeeds.length || actionSpeeds.length) && (
            <>
              <h3 styleName="dsv.header">API performance</h3>
              <p>Learn more about the time required to run your {props.service.name} actions.</p>
            </>
          )}
          {!!triggerSpeeds.length && (
            <div styleName="dsv.chart-container">
              <h3 styleName="dsv.header">Trigger performance</h3>
              <PerformanceTriggerActionChart
                categories={[
                  {
                    key: "delay",
                    label: "Delay to IFTTT",
                    color: "#999999",
                  },
                  {
                    key: "ifttt",
                    label: "IFTTT processing",
                    color: "#0099ff",
                  },
                  {
                    key: "service",
                    label: "Trigger endpoint",
                    color: props.service.brand_color,
                  },
                ]}
                speeds={triggerSpeeds}
                height={BASE_PERFORMANCE_CHART_HEIGHT + 14 * triggerSpeeds.length + 18 * (triggerSpeeds.length - 1)}
              />
            </div>
          )}
          {!!actionSpeeds.length && (
            <div styleName="dsv.chart-container">
              <h3 styleName="dsv.header">Action performance</h3>
              <PerformanceTriggerActionChart
                categories={[
                  {
                    key: "delay",
                    label: "Delay to IFTTT",
                    color: "#999999",
                  },
                  {
                    key: "ifttt",
                    label: "IFTTT processing",
                    color: "#0099ff",
                  },
                  {
                    key: "service",
                    label: "Action endpoint",
                    color: props.service.brand_color,
                  },
                ]}
                speeds={actionSpeeds}
                height={BASE_PERFORMANCE_CHART_HEIGHT + 14 * actionSpeeds.length + 18 * (actionSpeeds.length - 1)}
              />
            </div>
          )}
          <h3 styleName="dsv.header">API health</h3>
          <p>
            An overview of the communication quality between IFTTT and {props.service.name}. Red indicates endpoint
            errors.
          </p>
          <div styleName="dsv.chart-container">
            <h3 styleName="dsv.header">Endpoint success</h3>
            <ErrorSuccessHistogramChart data={props.successData} />
            <p styleName="dsv.note">
              All times are in your{" "}
              <a className="link-inline" target="_blank" href="/settings">
                local timezone
              </a>
              .
            </p>
          </div>
        </section>
      ) : (
        <>
          {applets === null && <Spinner config={{ top: "75%" }} />}
          {applets?.length === 0 && (
            <div className="diy-section">
              <a href={props.urls.diyCreationPath} className="cta-your-own-applet" onClick={logDiyClick}>
                <span>
                  No Applets found.
                  <br />
                  Make your own from scratch!
                </span>
                <button className="button-secondary">Create</button>
              </a>
            </div>
          )}
          {applets?.length > 0 && (
            <h3 styleName="dsv.services">Popular {props.service.name} workflows &amp; automations</h3>
          )}
          <section className="discover_services">
            <ul className="web-applet-cards" data-testid="discover-applets">
              {applets?.map((applet, idx) => {
                return <DiscoverServiceAppletCard key={idx} applet={applet} position={idx} />
              })}
            </ul>
          </section>
        </>
      )}
      {props.service.works_well_with_channels?.length > 0 && (
        <>
          <section styleName="dsv.service-works-well-with">
            <h4 className="works-well-with">Connect {props.service.name} to these services and more</h4>
            <RelatedServices
              services={props.service.works_well_with_channels}
              location="service_page"
              path={relatedService => {
                const [s1, s2] = [props.service.module_name, relatedService.module_name].sort()
                return `/connect/${s1}/${s2}`
              }}
            />
          </section>
        </>
      )}
    </>
  )
}

DiscoverServiceView.tqaShape = PropTypes.shape({
  name: PropTypes.string.isRequired,
  module_name: PropTypes.string.isRequired,
  description: PropTypes.string.isRequired,
  tier: PropTypes.string.isRequired,
})

DiscoverServiceView.propTypes = {
  healthViewEnabled: PropTypes.bool.isRequired,
  service: serviceShape.isRequired,
  urls: PropTypes.shape({
    diyCreationPath: PropTypes.string.isRequired,
    discoverJsonPath: PropTypes.string.isRequired,
    detailsPath: PropTypes.string.isRequired,
    myAppletsPath: PropTypes.string.isRequired,
    servicePath: PropTypes.string.isRequired,
    healthPath: PropTypes.string.isRequired,
  }),
  descriptions: PropTypes.shape({
    details: PropTypes.string.isRequired,
    myApplets: PropTypes.string.isRequired,
    service: PropTypes.string.isRequired,
    health: PropTypes.string.isRequired,
  }),
  currentTab: PropTypes.string.isRequired,
  triggers: PropTypes.arrayOf(DiscoverServiceView.tqaShape).isRequired,
  queries: PropTypes.arrayOf(DiscoverServiceView.tqaShape).isRequired,
  actions: PropTypes.arrayOf(DiscoverServiceView.tqaShape).isRequired,
  topAppletSlugs: PropTypes.arrayOf(PropTypes.string).isRequired,
  loggedOutDiscoverApplets: PropTypes.arrayOf(connectionShape),
  successData: PropTypes.arrayOf(
    PropTypes.shape({
      ratio: PropTypes.number.isRequired,
      time: PropTypes.string.isRequired,
    }).isRequired
  ),
  triggerSpeeds: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      delay: PropTypes.number,
      service: PropTypes.number,
      ifttt: PropTypes.number,
      total: PropTypes.number,
    })
  ).isRequired,
  actionSpeeds: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      delay: PropTypes.number,
      service: PropTypes.number,
      ifttt: PropTypes.number,
      total: PropTypes.number,
    })
  ).isRequired,
}

DiscoverServiceView.defaultProps = {
  applets: [],
}
